/**
 * Checks if an error indicates a permission denied or unauthorized error.
 * These errors should not be retried as they require user action.
 */
export function isPermissionDeniedError(error: Error) {
  const message = String(error.message ?? '')
  // ConnectError stringifies its code as `[permission_denied] …`, which neither the
  // camelCase nor the `unauthorized` pattern matches — without it, every denied RPC
  // burns three backoff rounds before the UI can say the user has no access.
  return (
    /PermissionDenied/i.test(message) ||
    /\[permission_denied\]/i.test(message) ||
    /unauthorized/i.test(message)
  )
}

/**
 * ConnectError stringifies a signed-out RPC as `[unauthenticated] …`.
 * The word "unauthorized" does not match that code, so the query would
 * otherwise back off three times before the page can ask the reader to sign in.
 */
export function isUnauthenticatedError(error: Error) {
  return /\[unauthenticated\]/i.test(String(error.message ?? ''))
}

/**
 * Checks if an error indicates a not-found resource.
 * These errors should not be retried as the resource won't appear on retry.
 */
export function isNotFoundError(error: Error) {
  const message = String(error.message ?? '')
  return /\[not_found\]/i.test(message) || /NotFound/i.test(message)
}

export function isRateLimitedError(error: Error) {
  const haystack = `${error.message} ${String(error)}`
  return /\bHTTP 429\b/i.test(haystack) || /\[resource_exhausted\]/i.test(haystack)
}

/**
 * Default retry function for React Query.
 * - Does not retry permission denied errors (keeps UI responsive)
 * - Does not retry not-found errors (resource won't appear on retry)
 * - Does not retry rate-limited errors (the server provides a cooldown)
 * - Retries up to 3 times for other errors
 */
export function retry(failureCount: number, error: unknown) {
  if (error instanceof Error) {
    if (isPermissionDeniedError(error)) {
      return false
    }

    if (isUnauthenticatedError(error)) {
      return false
    }

    if (isNotFoundError(error)) {
      return false
    }

    if (isRateLimitedError(error)) {
      return false
    }
  }

  return failureCount < 3
}
